/**
 * coinWalletService — the staff-side bridge to a client's SLIK Coin wallet. (Jose 2026-10-31)
 *
 * The canonical coin balance lives in `clients/{uid}.loyaltyPoints` (the SAME field both customer apps
 * display and the welcome-reward CF awards — see functions/src/index.ts). The staff checkout used to hardcode
 * the balance to 0, so coins NEVER showed at the register (Rahul had 500, staff saw 0). This reads the REAL
 * balance for an appointment's client and debits it on redemption. 100 coins = $1 (engagementConfig.coinValueCents).
 *
 * phoneKey fallback: imported appts sometimes point at a stub client record (0 coins) while the client's real
 * account — holding the coins — shares the phone. So if the appt's clientId has no coins, we look up the
 * coin-holding account by phoneKey and redeem from THAT one (never show/debit the wrong record).
 */
import {
  doc, getDoc, collection, query, where, getDocs, addDoc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface CoinWallet {
  balance: number;       // loyaltyPoints on the resolved account
  accountId: string | null; // the clients/{id} doc that holds the coins (what we debit)
}

const last10 = (p?: string): string => { const d = String(p || '').replace(/\D/g, ''); return d.length >= 10 ? d.slice(-10) : ''; };

/**
 * Resolve the coin wallet for an appointment's client. Primary = clients/{clientId}.loyaltyPoints; if that's
 * empty and we have a phoneKey, fall back to the max-coin account sharing that phone (the wrong-record case).
 */
export async function getWalletForClient(clientId?: string, phone?: string): Promise<CoinWallet> {
  let balance = 0;
  let accountId: string | null = clientId || null;
  try {
    if (clientId) {
      const snap = await getDoc(doc(db, 'clients', clientId));
      if (snap.exists()) balance = Number(snap.data()?.loyaltyPoints) || 0;
    }
  } catch (e) { console.error('[coinWallet] primary read failed', e); }

  if (balance <= 0) {
    const pk = last10(phone);
    if (pk.length === 10) {
      try {
        const qs = await getDocs(query(collection(db, 'clients'), where('phoneKey', '==', pk)));
        qs.forEach((d) => {
          const lp = Number(d.data()?.loyaltyPoints) || 0;
          if (lp > balance) { balance = lp; accountId = d.id; }
        });
      } catch (e) { console.error('[coinWallet] phoneKey fallback failed', e); }
    }
  }
  return { balance, accountId };
}

/**
 * Debit `coins` from the account's wallet in a transaction (guards against double-spend / a stale read), and
 * log a loyalty_transactions row for the audit trail. Returns the new balance. Throws if the balance is short.
 */
export async function redeemCoins(
  accountId: string,
  coins: number,
  context: { appointmentId?: string; clientName?: string; dollarValue: number }
): Promise<number> {
  if (!accountId || coins <= 0) return 0;
  const ref = doc(db, 'clients', accountId);
  const newBalance = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = Number(snap.data()?.loyaltyPoints) || 0;
    if (cur < coins) throw new Error(`Insufficient coins: have ${cur}, need ${coins}`);
    const next = cur - coins;
    tx.update(ref, { loyaltyPoints: next });
    return next;
  });
  try {
    await addDoc(collection(db, 'loyalty_transactions'), {
      clientId: accountId,
      clientName: context.clientName || '',
      type: 'redeem',
      coins: -coins,
      dollarValue: context.dollarValue,
      appointmentId: context.appointmentId || '',
      source: 'checkout',
      balanceAfter: newBalance,
      createdAt: serverTimestamp(),
    });
  } catch (e) { console.error('[coinWallet] redeem log failed (balance already debited)', e); }
  return newBalance;
}
